import type { ReactNode } from 'react';
import type { IconName } from '@circles/tokens';
import { Icon } from './Icon';
import type { AvatarHue } from '../data/mock';
import { usePerson } from '../data/people';
import { useActiveTheme } from '../state/theme';

export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------------------------

export function initials(name: string): string {
  const parts = name.replace(/^Dr\.\s*/, '').split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')
  ).toUpperCase();
}

export function Avatar({
  name,
  hue,
  size = 40,
  online,
  group,
}: {
  name: string;
  hue: AvatarHue;
  size?: number;
  online?: boolean;
  group?: boolean;
}) {
  return (
    <span
      className={cx('avatar', group && 'avatar-group')}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.38),
        background: `var(--color-avatar-${hue}-background)`,
        color: `var(--color-avatar-${hue}-foreground)`,
      }}
      aria-hidden
    >
      {group ? <Icon name="group" size={Math.round(size * 0.48)} /> : initials(name)}
      {online && <span className="avatar-online" />}
    </span>
  );
}

export function PersonAvatar({ id, size }: { id: string; size?: number }) {
  const p = usePerson(id);
  if (p.photo) return <PhotoAvatar hue={p.avatar} size={size ?? 40} />;
  return <Avatar name={p.name} hue={p.avatar} size={size} online={p.online} />;
}

/** Placeholder for a profile photo: a soft portrait, so the prototype ships no real photos. */
export function PhotoAvatar({ hue, size }: { hue: AvatarHue; size: number }) {
  return (
    <span className="avatar avatar-photo" style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <rect width="100" height="100" fill={`var(--color-avatar-${hue}-background)`} />
        <circle
          cx="50"
          cy="40"
          r="19"
          fill={`var(--color-avatar-${hue}-foreground)`}
          opacity=".85"
        />
        <path
          d="M14 100c4-22 18-32 36-32s32 10 36 32z"
          fill={`var(--color-avatar-${hue}-foreground)`}
          opacity=".7"
        />
      </svg>
    </span>
  );
}

/** The organisation logo, or the Circles logo in the neutral design. */
export function OrgLogo({ size = 28 }: { size?: number }) {
  const theme = useActiveTheme();
  if (theme.logo) {
    return (
      <img
        className="org-logo"
        src={theme.logo}
        width={size}
        height={size}
        alt={theme.orgName ?? 'Circles'}
      />
    );
  }
  return (
    <span
      className="org-logo org-monogram"
      style={{ width: size, height: size, fontSize: size * 0.5 }}
    >
      {theme.monogram}
    </span>
  );
}

export function ExternalLabel({ org }: { org: string }) {
  return (
    <span className={cx('external-label', org === 'Guest' && 'external-guest')}>
      {org === 'Guest' ? 'Guest' : org}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------

export function IconButton({
  icon,
  label,
  onClick,
  active,
  size = 22,
  className,
  filled,
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  active?: boolean;
  size?: number;
  className?: string;
  filled?: boolean;
}) {
  return (
    <button
      type="button"
      className={cx('icon-button', active && 'is-active', className)}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      <Icon name={icon} size={size} filled={filled} />
    </button>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  icon,
  disabled,
  block,
  small,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger';
  icon?: IconName;
  disabled?: boolean;
  block?: boolean;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      className={cx(
        'button',
        `button-${variant}`,
        block && 'button-block',
        small && 'button-small',
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <Icon name={icon} size={small ? 16 : 18} />}
      {children}
    </button>
  );
}

export function Badge({ count, muted }: { count: number; muted?: boolean }) {
  if (count <= 0) return null;
  return <span className={cx('badge', muted && 'badge-muted')}>{count > 99 ? '99+' : count}</span>;
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx('toggle', checked && 'is-on')}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-knob" />
    </button>
  );
}

export function SearchField({
  value,
  onChange,
  placeholder = 'Search',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="search-field">
      <Icon name="search" size={17} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      {value && (
        <button
          type="button"
          className="search-clear"
          onClick={() => onChange('')}
          aria-label="Clear search"
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={cx('segmented', className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          className={cx('segmented-item', value === o.value && 'is-selected')}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Chip({
  children,
  selected,
  onClick,
}: {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={cx('chip', selected && 'is-selected')}
      onClick={onClick}
      aria-pressed={selected}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------------------------

/** Top bar of a screen. `large` shows the tab title style used at the root of each tab. */
export function NavBar({
  title,
  subtitle,
  onBack,
  leading,
  trailing,
  large,
  center,
  onTitleClick,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  leading?: ReactNode;
  trailing?: ReactNode;
  large?: boolean;
  center?: boolean;
  onTitleClick?: () => void;
}) {
  return (
    <header className={cx('nav-bar', large && 'nav-bar-large', center && 'nav-bar-center')}>
      {onBack && (
        <button type="button" className="nav-back" onClick={onBack} aria-label="Back">
          <Icon name="back" size={26} strokeWidth={2} />
        </button>
      )}
      {leading}
      <div className={cx('nav-title-wrap', onTitleClick && 'is-clickable')} onClick={onTitleClick}>
        {title && <h1 className="nav-title">{title}</h1>}
        {subtitle && <div className="nav-subtitle">{subtitle}</div>}
      </div>
      {trailing && <div className="nav-trailing">{trailing}</div>}
    </header>
  );
}

export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
  icon,
  danger,
  chevron,
  selected,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  icon?: IconName;
  danger?: boolean;
  chevron?: boolean;
  selected?: boolean;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={cx(
        'list-row',
        danger && 'is-danger',
        selected && 'is-selected',
        onClick && 'is-clickable',
      )}
      onClick={onClick}
    >
      {icon && (
        <span className="list-row-icon">
          <Icon name={icon} size={20} />
        </span>
      )}
      {leading}
      <span className="list-row-text">
        <span className="list-row-title">{title}</span>
        {subtitle && <span className="list-row-subtitle">{subtitle}</span>}
      </span>
      {trailing}
      {chevron && <Icon name="chevronRight" size={18} className="list-row-chevron" />}
    </Tag>
  );
}

export function Section({
  title,
  children,
  footer,
}: {
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="section">
      {title && <h2 className="section-title">{title}</h2>}
      <div className="section-body">{children}</div>
      {footer && <p className="section-footer">{footer}</p>}
    </section>
  );
}

export function EmptyState({
  icon,
  title,
  text,
}: {
  icon: IconName;
  title: string;
  text?: string;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">
        <Icon name={icon} size={30} />
      </span>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

export function Fab({
  icon,
  label,
  onClick,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="fab" onClick={onClick} aria-label={label} title={label}>
      <Icon name={icon} size={24} strokeWidth={2} />
    </button>
  );
}

export function Sheet({
  children,
  onClose,
  title,
  className,
}: {
  children: ReactNode;
  onClose: () => void;
  title?: string;
  className?: string;
}) {
  return (
    <div className="sheet-layer" onClick={onClose}>
      <div
        className={cx('sheet', className)}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={title}
      >
        <span className="sheet-grabber" />
        {title && <h2 className="sheet-title">{title}</h2>}
        {children}
      </div>
    </div>
  );
}
